// =============================================
// COMPONENTE HEADER - CABEÇALHO
// =============================================
// Arquivo: Header.tsx
// Descrição: Componente de cabeçalho com ações do usuário
// =============================================

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Header.css';
import { apiService } from '../../services/api';

// =============================================
// INTERFACES
// =============================================

interface HeaderProps {
  title: string;
  onMenuToggle: () => void;
}

interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  timestamp: Date;
  read: boolean;
  link?: string;
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

/**
 * Componente Header - Cabeçalho do sistema
 *
 * @param title - Título da página atual
 * @param onMenuToggle - Função para alternar menu mobile
 * @returns JSX.Element - Componente de cabeçalho
 */
const Header: React.FC<HeaderProps> = ({ title, onMenuToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Refs para detectar cliques fora
  const notificationRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // =============================================
  // CARREGAR NOTIFICAÇÕES DA API
  // =============================================

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await apiService.getNotifications({ limit: 10 });
        if (response.success && response.data) {
          const mappedNotifications: Notification[] = response.data.map((notif: any) => ({
            id: notif.id,
            title: notif.titulo,
            message: notif.mensagem,
            type: mapTypeToFrontend(notif.tipo),
            timestamp: new Date(notif.data_envio),
            read: notif.lida,
            link: notif.link
          }));
          setNotifications(mappedNotifications);
        }
      } catch (error) {
        console.error('Erro ao buscar notificações:', error);
      }
    };

    fetchNotifications();

    // Atualizar notificações a cada 30 segundos
    const interval = setInterval(fetchNotifications, 30000);

    return () => clearInterval(interval);
  }, []);

  // =============================================
  // MAPEAR TIPO PARA FRONTEND
  // =============================================

  const mapTypeToFrontend = (tipo: string): 'info' | 'warning' | 'error' | 'success' => {
    const typeMap: Record<string, 'info' | 'warning' | 'error' | 'success'> = {
      'success': 'success',
      'error': 'error',
      'warning': 'warning',
      'info': 'info',
      'execution': 'info',
      'system': 'info',
      'queue': 'warning',
      'user': 'info',
      'performance': 'warning'
    };

    return typeMap[tipo] || 'info';
  };

  // =============================================
  // MARCAR NOTIFICAÇÃO COMO LIDA
  // =============================================

  const markNotificationAsRead = async (notificationId: number) => {
    try {
      await apiService.markNotificationAsRead(notificationId);

      // Atualizar estado local
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  // =============================================
  // MARCAR TODAS COMO LIDAS
  // =============================================

  const markAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();

      // Atualizar estado local
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error);
    }
  };

  // =============================================
  // NAVEGAR PARA LINK DA NOTIFICAÇÃO
  // =============================================

  const handleNotificationClick = (notification: Notification) => {
    // Marcar como lida se não estiver
    if (!notification.read) {
      markNotificationAsRead(notification.id);
    }

    // Navegar para o link se existir
    if (notification.link) {
      navigate(notification.link);
      setShowNotifications(false);
    }
  };

  // =============================================
  // MANIPULAÇÃO DE EVENTOS
  // =============================================

  /**
   * Detecta cliques fora dos dropdowns e os fecha
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Verifica se o clique foi fora do dropdown de notificações
      if (
        notificationRef.current &&
        !notificationRef.current.contains(target) &&
        showNotifications
      ) {
        setShowNotifications(false);
      }

      // Verifica se o clique foi fora do dropdown do usuário
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(target) &&
        showUserMenu
      ) {
        setShowUserMenu(false);
      }
    };

    // Adiciona o listener apenas se algum dropdown estiver aberto
    if (showNotifications || showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Remove o listener quando o componente desmontar ou os dropdowns fecharem
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications, showUserMenu]);

  // =============================================
  // MANIPULAÇÃO DE EVENTOS
  // =============================================

  /**
   * Alterna visibilidade das notificações
   */
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    setShowUserMenu(false);
  };

  /**
   * Alterna menu do usuário
   */
  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
    setShowNotifications(false);
  };

  /**
   * Fecha todos os menus dropdown
   */
  const closeAllMenus = () => {
    setShowNotifications(false);
    setShowUserMenu(false);
  };

  /**
   * Calcula tempo relativo para exibição
   * @param date - Data para calcular
   * @returns string - Tempo relativo formatado
   */
  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Agora mesmo';
    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours} h`;
    if (diffDays < 7) return `${diffDays} d`;
    return date.toLocaleDateString();
  };

  /**
   * Obtém iniciais do nome do usuário
   * @param name - Nome completo
   * @returns string - Iniciais
   */
  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  const unreadNotifications = notifications.filter(n => !n.read).length;

  return (
    <div className="header" onClick={closeAllMenus}>
      {/* Lado Esquerdo - Título e Botão Mobile */}
      <div className="header-left">
        <button className="mobile-menu-btn" onClick={onMenuToggle} title="Abrir menu">
          <i className="fas fa-bars"></i>
        </button>
        <h1>{title}</h1>
      </div>

      {/* Lado Direito - Ações do Usuário */}
      <div className="header-actions">
        {/* Botão de Notificações */}
        <div className="notification-container" ref={notificationRef}>
          <button 
            className="notification-btn"
            onClick={(e) => {
              e.stopPropagation();
              toggleNotifications();
            }}
            title="Notificações"
          >
            <i className="fas fa-bell"></i>
            {unreadNotifications > 0 && (
              <span className="notification-badge">{unreadNotifications}</span>
            )}
          </button>

          {/* Dropdown de Notificações */}
          {showNotifications && (
            <div className="notification-dropdown" onClick={e => e.stopPropagation()}>
              <div className="notification-header">
                <h3>Notificações</h3>
                <div className="notification-header-actions">
                  <span className="notification-count">{unreadNotifications} não lidas</span>
                  {unreadNotifications > 0 && (
                    <button
                      className="mark-all-read-btn"
                      onClick={markAllAsRead}
                      title="Marcar todas como lidas"
                    >
                      <i className="fas fa-check-double"></i>
                    </button>
                  )}
                </div>
              </div>
              
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div className="notification-empty">
                    <i className="fas fa-bell-slash"></i>
                    <p>Nenhuma notificação</p>
                  </div>
                ) : (
                  notifications.map(notification => (
                    <div
                      key={notification.id}
                      className={`notification-item ${notification.read ? 'read' : 'unread'} ${notification.type} ${notification.link ? 'clickable' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                      style={{ cursor: (notification.link || !notification.read) ? 'pointer' : 'default' }}
                      title={notification.link ? `Clique para ir para: ${notification.link}` : undefined}
                    >
                      <div className="notification-icon">
                        <i className={`fas ${
                          notification.type === 'warning' ? 'fa-exclamation-triangle' :
                          notification.type === 'error' ? 'fa-times-circle' :
                          notification.type === 'success' ? 'fa-check-circle' :
                          'fa-info-circle'
                        }`}></i>
                      </div>
                      <div className="notification-content">
                        <h4>{notification.title}</h4>
                        <p>{notification.message}</p>
                        <span className="notification-time">
                          {getRelativeTime(notification.timestamp)}
                        </span>
                      </div>
                      {!notification.read && (
                        <div className="notification-dot"></div>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div className="notification-footer">
                <button
                  className="view-all-btn"
                  onClick={() => {
                    navigate('/notifications');
                    setShowNotifications(false);
                  }}
                >
                  Ver todas as notificações
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Perfil do Usuário */}
        <div className="user-profile-container" ref={userMenuRef}>
          <div 
            className="user-profile"
            onClick={(e) => {
              e.stopPropagation();
              toggleUserMenu();
            }}
          >
            <div className="user-avatar">
              {user?.foto_perfil_url ? (
                <img src={user.foto_perfil_url} alt="Foto de perfil" className="user-avatar-img" />
              ) : (
                user?.nome ? getUserInitials(user.nome) : 'U'
              )}
            </div>
            <div className="user-info">
              <h3>{user?.nome || 'Usuário'}</h3>
              <p>{user?.tipo ? user.tipo.charAt(0).toUpperCase() + user.tipo.slice(1) : 'Usuário'}</p>
            </div>
            <i className={`fas fa-chevron-down ${showUserMenu ? 'expanded' : ''}`}></i>
          </div>

          {/* Dropdown do Usuário */}
          {showUserMenu && (
            <div className="user-dropdown" onClick={e => e.stopPropagation()}>
              <div className="user-dropdown-header">
                <div className="user-avatar large">
                  {user?.foto_perfil_url ? (
                    <img src={user.foto_perfil_url} alt="Foto de perfil" className="user-avatar-img" />
                  ) : (
                    user?.nome ? getUserInitials(user.nome) : 'U'
                  )}
                </div>
                <div className="user-details">
                  <h3>{user?.nome || 'Usuário'}</h3>
                  <p>{user?.email || ''}</p>
                  <span className="user-role">
                    {user?.tipo ? user.tipo.charAt(0).toUpperCase() + user.tipo.slice(1) : 'Usuário'}
                  </span>
                </div>
              </div>

              <div className="user-dropdown-menu">
                <button className="dropdown-item" onClick={() => { navigate('/perfil'); setShowUserMenu(false); }}>
                  <i className="fas fa-user"></i>
                  Meu Perfil
                </button>
                <button className="dropdown-item" onClick={() => { navigate('/configuracoes'); setShowUserMenu(false); }}>
                  <i className="fas fa-cog"></i>
                  Configurações
                </button>
                <button className="dropdown-item" onClick={() => { navigate('/privacidade'); setShowUserMenu(false); }}>
                  <i className="fas fa-shield-alt"></i>
                  Privacidade
                </button>

                <div className="dropdown-divider"></div>

                <button className="dropdown-item" onClick={logout}>
                  <i className="fas fa-sign-out-alt"></i>
                  Sair
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
